import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

/**
 * i18n plumbing (Build Guide §1.3). This sets up the machinery only — strings
 * are extracted into these namespaces as each component is rebuilt, and the RO
 * catalogue is filled in the translation pass. Importing this module once (in
 * index.tsx, before render) initializes the shared default instance that
 * `useTranslation` / `<Trans>` read from.
 */

export const NAMESPACES = [
  "common",
  "auth",
  "bets",
  "matches",
  "profile",
  "admin",
  "assistant",
  "notifications",
  "errors",
] as const;

export const SUPPORTED_LANGUAGES = ["en", "ro"] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * Maps any i18next language tag ("ro", "ro-RO", "en-US", undefined) to the
 * backend AI `lang` code. Single source of truth shared by the imperative
 * fetch layer (assistant.service `currentLang`) and the React Query keys
 * (`useAiLang`) so the two can never disagree — a mismatch would let the
 * cache key say "ro" while the request asks for "en", or vice-versa, which
 * is exactly how the AI cards used to get stuck on the wrong language after
 * an in-session switch.
 */
export function toAiLang(language: string | null | undefined): Language {
  return (language ?? "en").toLowerCase().startsWith("ro") ? "ro" : "en";
}

/** Key i18next persists the chosen language under (raw string, not JSON). */
export const LOCALE_STORAGE_KEY = "br-locale";

// EN resource stubs. Real copy is migrated namespace-by-namespace as the
// matching pages are rebuilt; empty namespaces fall back to the key.
const en = {
  common: {
    language: { en: "EN", ro: "RO", label: "Language" },
    theme: { toggle: "Toggle dark theme", label: "Theme" },
    sports: {
      football: "Football",
      basketball: "Basketball",
      tennis: "Tennis",
      handball: "Handball",
      hockey: "Hockey",
      baseball: "Baseball",
      rugby: "Rugby",
      americanfootball: "American Football",
      volleyball: "Volleyball",
    },
    // Feed market names + bet sides. Keys are the EXACT English feed
    // strings; see utils/marketLabels.ts. EN is identity (verbatim feed).
    market: {
      names: {
        "Match Winner": "Match Winner",
        Winner: "Winner",
        "Home/Away": "Home/Away",
        "1st Half Winner": "1st Half Winner",
        "2nd Half Winner": "2nd Half Winner",
        "Goals Over/Under": "Goals Over/Under",
        "Goals Over/Under 1st Half": "Goals Over/Under 1st Half",
        "Over/Under": "Over/Under",
        "Over/Under by Games in Match": "Over/Under by Games in Match",
        "Asian Handicap": "Asian Handicap",
        "Asian Handicap (Sets)": "Asian Handicap (Sets)",
        "Asian Handicap (Games)": "Asian Handicap (Games)",
        "Set Betting": "Set Betting",
        "Total - Home": "Total - Home",
        "Total - Away": "Total - Away",
        "Corners Over Under": "Corners Over Under",
        "HT/FT Double": "HT/FT Double",
        "Both Teams To Score": "Both Teams To Score",
        "Double Chance": "Double Chance",
        "Highest Scoring Half": "Highest Scoring Half",
        "Odd/Even": "Odd/Even",
        "Correct Score": "Correct Score",
        "Draw No Bet": "Draw No Bet",
      },
      sides: {
        Home: "Home",
        Away: "Away",
        Draw: "Draw",
        Over: "Over",
        Under: "Under",
        Yes: "Yes",
        No: "No",
        Odd: "Odd",
        Even: "Even",
        "1st Half": "1st Half",
        "2nd Half": "2nd Half",
        "Home/Draw": "Home/Draw",
        "Draw/Away": "Draw/Away",
        "Home/Away": "Home/Away",
      },
      categories: {
        Main: "Main",
        "Over/Under": "Over/Under",
        Handicap: "Handicap",
        Players: "Players",
        Combo: "Combo",
      },
    },
    nav: {
      betfeed: "Betfeed",
      matches: "Matches",
      standings: "Standings",
      bookmakers: "Bookmakers",
      learn: "Learn",
      admin: "Dashboard",
    },
    userMenu: { myProfile: "My profile", logout: "Log out" },
    footer: {
      telegramPitch:
        "Come to the Telegram channel where you receive instant notifications every time the best tipsters post a new tip",
      joinTelegram: "Join us on Telegram",
      usefulPages: "Useful pages",
      contact: "Contact",
      terms: "Terms and conditions",
      privacy: "Privacy policy",
      followUs: "Follow us",
    },
    whatsNew: {
      title: "What's new on V2",
      heading: "What's new on V2.0",
      cta: "Let's go!",
      features: [
        "🤖 AI match briefings — form, H2H & suggested picks",
        "🤖 AI Ticket Builder & Pattern Matcher",
        "🤖 Match of the Day AI picks",
        "🤖 “Bettors like you” — personalised discovery",
        "🤖 Personal Trend: your hot/cold insights",
        "🤖 Community crowd-check on every match",
        "🤖 Pre-bet sanity check & smart stake",
        "🌙 Dark mode",
        "🌐 Romanian language support",
        "🆕 Added most important Bookmakers",
        "🆕 Comparison & Bookmakers filters",
        "🆕 You can select the best odd for your favorite bet",
        "🆕 More betting markets for all sports",
        "🆕 Browser Notifications",
        "🆕 Reset bankroll max 3 times - admin approval",
        "🆕 Upload profile photo",
        "🆕 Responsive design on mobile and tablet",
      ],
    },
    standings: {
      title: "Best bettors",
      eyebrow: "Leaderboard",
      tagline: "Ranked by verified, settled performance. Follow the sharpest and copy their reads.",
      empty: "No bettors yet",
      emptyDesc: "Once bettors settle tickets, the leaderboard fills in here.",
      winRate: "Win rate",
      tickets: "Tickets",
      scopeAllTime: "All time",
      scope90: "90 days",
      scope30: "30 days",
      scopeAria: "Time range",
      loading: "Loading standings",
      subtitle: "General standing",
      loadMore: "Load more",
    },
    bookmakers: {
      title: "Top Bookmakers",
      loadError: "Couldn't load bookmakers",
      loadErrorDesc: "Something went wrong fetching the list. Please try again in a moment.",
      empty: "No bookmakers yet",
      emptyDesc: "Check back soon — bookmakers will appear here once available.",
      featured: "Featured",
      featuredRated: "Featured · #1 rated",
      moreBookmakers: "More bookmakers",
      rated: "#{{rank}} rated",
      visitNamed: "Visit {{name}}",
      readReview: "Read review",
      metricRating: "Rating",
      metricPayout: "Payout",
      metricSpeed: "Payout speed",
      visitSite: "Visit site",
      noReviews: "No reviews yet",
      reviewsCount_one: "{{count}} review",
      reviewsCount_other: "{{count}} reviews",
      filtersAria: "Filter bookmakers",
      filterHighRated: "High-rated",
      filterTopPayout: "Top payout",
      filterHasBonus: "Has bonus",
      noMatch: "No bookmakers match",
      noMatchDesc: "Try removing a filter to see more.",
      reviewsTitle: "{{name}} reviews",
      writeReview: "Write a review",
      editReview: "Edit your review",
      yourRating: "Your rating",
      starAria: "Rate {{n}} stars",
      reviewPlaceholder: "Share your experience (optional)",
      submitReview: "Submit review",
      updateReview: "Update review",
      deleteReview: "Delete review",
      reviewError: "Couldn't save your review. Please try again.",
      reviewsLoading: "Loading reviews…",
      noReviewsYet: "No reviews yet. Be the first to review.",
      you: "You",
      loadMore: "Load more",
    },
    learn: {
      title: "Learn",
      subtitle: "Market explanations and betting concepts to sharpen your reads.",
      loadError: "Couldn't load Learn",
      loadErrorDesc: "Check your connection and try again.",
      empty: "Nothing here yet",
      emptyDesc: "Learning content will appear here once it's published.",
      articles: "Articles",
      formula: "Formula",
      workedExample: "Worked example",
      related: "Related",
    },
    chrome: {
      logoAlt: "BetRight",
      openMenu: "Open menu",
      openProfileMenu: "Open profile menu",
      openUserMenu: "Open user menu",
      openSearch: "Open search",
      openTicket: "Open ticket",
      closeDialog: "Close dialog",
      home: "Home",
      notifications: "Notifications",
      all: "All",
      orderByLabel: "Order by:",
      orderBy: { PROFIT: "Profit", ROI: "ROI", YIELD: "Yield" },
      allSports: "All sports",
      allSportsTooltip: "All Sports",
      moreSports: "More sports",
      allBookmakersTooltip: "All Bookmakers",
      moreBookmakers: "More bookmakers",
      comparison: "COMPARISON",
      previousDay: "Previous day",
      nextDay: "Next day",
      brief: "Brief",
      removeFromTicket: "Remove from ticket",
      ticketAdded: "Ticket has been successfully added",
      ticketAddedAdjusted: "Placed at final odds {{odd}} — potential return {{payout}} U",
      insufficientBankroll: "Insufficient bankroll. Please request bankroll reset",
      maxBetsLimit: "You have reached the maximum limit of {{max}} matches",
      social: {
        facebook: "BetRight on Facebook",
        instagram: "BetRight on Instagram",
        tiktok: "BetRight on TikTok",
      },
      notifPrompt: {
        title: "Enable browser notifications?",
        yes: "Yes",
        no: "No",
      },
      push: {
        activated: "Push notifications have been activated",
        denied: "Permission denied.",
        deniedManual: "Permission denied. You can manually activate notifications from browser",
        deniedSettings:
          "Permission was denied. You'll need to enable notifications for this site in your browser settings.",
        notSupported: "Push notifications are not supported in this browser.",
        notConfigured: "Push notifications are not configured for this build.",
        enabled: "Push notifications enabled.",
        disabled: "Push notifications disabled.",
      },
    },
    errorPages: {
      pageError: "Something went wrong on this page.",
      pageErrorHint: "We've logged the error. You can retry — recent data will be re-fetched.",
      tryAgain: "Try again",
      errorTitle: "Something went wrong",
      errorDesc:
        "Sorry, there was a problem processing your action. We're working to fix it as soon as possible.",
      forbidden: "Forbidden",
      forbiddenDesc: "You don't have access to this page.",
      notFound: "Page not found",
      notFoundDesc: "The page you're trying to reach isn't available.",
      backHome: "Back to home page",
    },
    blog: {
      comingSoon: "Blog coming soon",
      comingSoonDesc: "We're still putting this together — check back shortly.",
    },
  },
  auth: {
    login: {
      title: "Login",
      welcomeTitle: "Welcome back",
      newUser: "New user?",
      registerLink: "Register",
      username: "User name",
      password: "Password",
      submit: "Login",
      forgot: "Forgot your password?",
    },
    register: {
      title: "Register",
      createTitle: "Create your account",
      haveAccount: "Do you already have an account?",
      loginLink: "Login",
      username: "Username",
      firstName: "First Name",
      lastName: "Last Name",
      email: "Email",
      password: "Password",
      confirmPassword: "Confirm password",
      submit: "Register",
      terms:
        "By creating the account I confirm that I am at least 18 years old and agree to the BETRIGHT Terms and Conditions and the Privacy Notice.",
    },
    forgot: {
      title: "Reset your password",
      intro: "Enter the email associated with your account.",
      email: "Email",
      submit: "Send reset link",
      backToLogin: "Back to login",
      sent: "If an account exists for that email, a reset link has been sent. Please check your inbox.",
      sentTitle: "Check your inbox",
      invalidEmail: "Please enter a valid email address.",
    },
    reset: {
      title: "Set a new password",
      subtitle: "Choose a strong password you don't use elsewhere.",
      newPassword: "New password",
      confirmPassword: "Confirm new password",
      submit: "Reset password",
      success: "Your password has been reset. Please sign in.",
      invalidTitle: "Reset link invalid",
      invalidToken: "This reset link is missing or malformed. Please request a new one.",
      requestNew: "Request a new link",
    },
    password: {
      show: "Show password",
      hide: "Hide password",
      weak: "Weak",
      fair: "Fair",
      strong: "Strong",
      hint: "Use 8+ characters with a number and a symbol.",
    },
    brand: {
      eyebrow: "Sharp bettors · since 2024",
      headlineLead: "Track every bet.",
      headlineEmphasis: "Bet like the sharps.",
      sub: "Follow the platform's most profitable tipsters, get AI reads on every fixture, and see your real ROI — not just your wins.",
      feature1: "Verified ROI on every tipster",
      feature2: "AI reads on every fixture",
      feature3: "Crowd & sharp-money signals",
      trust: "Joined by sharp bettors tracking real ROI every day.",
    },
  },
  bets: {
    filter: {
      latest: "Latest",
      following: "Following",
      winners: "Winners",
      top: "Top",
      tipLatest: "Most recent betting tickets",
      tipFollowing: "See the tickets of those you follow",
      tipFollowingEmpty: "You are not following any user",
      tipWinners: "See the winning tickets",
      tipTop: "Most-reacted tickets in the last 24h",
    },
    loadMore: "Load more",
    loading: "Loading...",
    loadingTickets: "Loading tickets",
    feed: {
      noTicketsFromSport: "No tickets from {{sport}}",
      filterEmptyDesc: "Try a different sport or clear the filter.",
      resetSportFilter: "Reset sport filter",
      noTicketsYet: "No tickets yet",
      noTicketsDesc: "Bets shared by you and the people you follow will show up here.",
    },
    ticket: {
      title: "Ticket",
      bankroll: "BANKROLL",
      odd: "Odd",
      totalOdd: "Total odd",
      potentialReturn: "Potential return",
      close: "Close",
      clearAll: "Clear all",
      emptyTitle: "Your ticket is empty",
      emptyHint: "Tap a match or pick to start building your ticket.",
      analysisPlaceholder: "Write ticket analysis (optional)",
      stake: "Stake",
      stakeRange: "(min: 0.5 - max: 100 U)",
      quickStake: "{{amount}} U",
      delete: "Delete ticket",
      place: "Place ticket",
      placeWithReturn: "Place ticket · {{amount}} U",
      removeFromTicket: "Remove from ticket",
    },
    row: { copyBet: "Copy bet", betAdded: "Bet added" },
    card: {
      stake: "STAKE",
      analysis: "Analysis",
      odd: "Odd",
      ticketRemoved: "Ticket successfully removed.",
      deleteTicket: "Delete ticket",
      removeTicket: "Remove this ticket",
      alreadyCopied: "Ticket already copied",
      copyTicket: "Copy ticket",
      copyTicketAria: "Copy this ticket to your builder",
      viewUser: "View user details",
      viewUserAria: "View {{name}}'s profile",
      unfollow: "Unfollow",
      unfollowAria: "Unfollow this user",
      follow: "Follow",
      followAria: "Follow this user",
      likes: "Ticket likes",
      unlike: "Unlike this ticket",
      like: "Like this ticket",
    },
  },
  matches: {
    title: "Schedule and matches",
    loading: "Loading matches",
    aiToolsError: "The AI tools couldn't load. Refresh the page to try again.",
    matchStarted: "This match has already started — you can no longer bet on it.",
    brief: "Brief",
    briefAria: "AI briefing for this match",
    briefForMatch: "AI briefing for {{home}} vs {{away}}",
    community: "Community",
    communityPulse: "Community pulse",
    betsCount_one: "{{count}} bet",
    betsCount_other: "{{count}} bets",
    bettorsCount_one: "{{count}} bettor",
    bettorsCount_other: "{{count}} bettors",
    crowdCheck: "Crowd check",
    crowdInsight_one:
      "{{count}} bettor has backed this match. The crowd leans {{pct}}% {{selection}}.",
    crowdInsight_other:
      "{{count}} bettors have backed this match. The crowd leans {{pct}}% {{selection}}.",
    crowdInsightSharp_one:
      "{{count}} bettor has backed this match. The crowd leans {{pct}}% {{selection}} — and sharp money agrees.",
    crowdInsightSharp_other:
      "{{count}} bettors have backed this match. The crowd leans {{pct}}% {{selection}} — and sharp money agrees.",
    crowdEmptyTitle: "Not enough bets yet",
    crowdEmptyDesc:
      "Community signal appears once bettors back this match — check back closer to kickoff.",
    mostBacked: "Most backed today",
    mostBackedEmpty: "No bets backed yet — check back closer to kickoff.",
    communityEmpty: "No fixtures trending yet.",
    last24h: "24h",
    backedCount_one: "{{count}} backing",
    backedCount_other: "{{count}} backing",
    openOdds: "Open odds for {{home}} vs {{away}}",
    openBriefingFor: "Open briefing for {{home}} vs {{away}}",
    sharp: "Sharp",
    seeAllOdds: "See all odds",
    surfaceAria: "Surface: {{label}}",
    surfaceLabel: {
      clay: "Clay",
      hard: "Hard",
      "hard-indoor": "Hard (Indoor)",
      grass: "Grass",
      carpet: "Carpet",
    },
    search: {
      topSearches: "Top Searches",
      placeholder: "Search bettors, matches, leagues…",
      close: "Close search",
      hintTitle: "Search BetRight",
      hintDesc: "Find bettors, matches and leagues.",
      noResults: "No results found",
      noResultsDesc: "Try a different search term.",
      noToday: "No matches today",
      noTodayDesc: "Check back later as more matches are scheduled.",
      roi: "ROI",
      groups: {
        bettors: "Bettors",
        matches: "Matches today",
        leagues: "Leagues",
      },
    },
    empty: {
      noMatchesSportDate: "No matches from {{sport}} in {{date}}",
      trySportDate: "Try a different sport or date.",
      resetSport: "Reset sport filter",
      noMatchesDate: "No matches in {{date}}",
      pickDate: "Pick another date from the date picker.",
    },
  },
  profile: {
    loading: "Loading profile",
    unavailable: "Profile unavailable",
    unavailableDesc: "We couldn't load your profile right now. Please refresh.",
    userNotFound: "User not found",
    userNotFoundDesc: "User {{name}} was not found.",
    backHome: "Back to home page",
    followersCount_one: "Follower: {{count}}",
    followersCount_other: "Followers: {{count}}",
    registeredIn: "Registered in {{date}}",
    requestReset_one: "Request stats reset {{count}}/3",
    requestReset_other: "Request stats reset {{count}}/3",
    editProfile: "Edit profile",
    signOutEverywhere: "Sign out everywhere",
    signOutConfirm:
      "Sign out from every device you're currently logged in on? You'll need to sign in again on each one.",
    resetRequestSent: "The request has been sent to the administrators.",
    enableNotifications: "Enable notifications",
    disableNotifications: "Disable notifications",
    follow: "Follow",
    unfollow: "Unfollow",
    following: "Following",
    followUserAria: "Follow {{name}}",
    unfollowUserAria: "Unfollow {{name}}",
    stat: {
      profit: "Profit",
      tickets: "Tickets",
      roi: "Roi",
      yield: "Yield",
      bankroll: "Bankroll",
    },
    tab: {
      winRate: "Win Rate",
      betsHistory: "Bets history",
      followers: "Followers",
      following: "Following",
      insights: "Insights",
    },
    table: {
      bettor: "Bettor",
      winRate: "Win rate",
    },
    winRate: {
      label: "Win rate {{rate}} %",
      wins_one: "Win: {{count}}",
      wins_other: "Wins: {{count}}",
      lost_one: "Lost: {{count}}",
      lost_other: "Lost: {{count}}",
      avgStake: "Average stake",
      avgOdd: "Average odd",
      chartTitle: "Win rate by month",
      chartAria: "Win rate by month",
    },
    strengths: {
      title: "Strengths by sport",
      empty: "Not enough settled bets to break down by sport yet.",
      wr: "{{rate}}% WR",
      roi: "{{roi}}% ROI",
      sample_one: "{{count}} bet",
      sample_other: "{{count}} bets",
    },
    ticketHistory: "Ticket history",
    bestUsers: "Best users",
    bettorsToFollow: "Bettors to follow",
    viewAll: "All",
    yourWeek: "Your week",
    ticketsCount_one: "{{count}} ticket",
    ticketsCount_other: "{{count}} tickets",
    searchUsers: "Search users",
    loadMore: "Load more",
    viewTips: "View tips",
    viewUser: "View user details",
    viewUserProfile: "View {{name}}'s profile",
    statInline: { profit: "Profit", roi: "ROI", yield: "YIELD" },
    editDropdown: "Edit profile",
    userActions: "User actions",
    changePassword: {
      title: "Change Password",
      current: "Current Password",
      newPassword: "New Password",
      confirm: "Confirm Password",
      cancel: "Cancel",
      save: "Save new password",
      success: "Password successfully updated.",
    },
    editProfileDialog: {
      title: "User data",
      changePassword: "Change Password",
      cancel: "Cancel",
      save: "Save Changes",
      firstName: "First name",
      lastName: "Last name",
      userName: "Username",
      email: "Email",
      dataSavedAdmin: "User data successfully updated.",
      dataSaved: "Successfully updated your profile info.",
      photoSavedAdmin: "User photo successfully updated.",
      photoSaved: "Successfully updated your profile photo.",
    },
    uploader: {
      invalidType: "Only JPEG, PNG, WebP, or GIF images are accepted.",
      tooLarge: "Image is too large (max {{size}}MB).",
      upload: "Upload profile photo",
    },
  },
  admin: {
    header: {
      eyebrow: "Admin",
      title: "Dashboard",
      status: "Feeds healthy · odds {{time}}",
    },
    announcement: {
      title: "Send Notification",
      placeholder: "Your announcement",
      send: "Send announcement",
      hint: "Pushed to every user's notification feed.",
      sent: "Announcement notification has been sent to all users.",
    },
    endpoints: {
      title: "Endpoint telemetry",
      today: "today",
      empty: "No endpoints have reported a successful run today yet.",
    },
    requests: {
      title: "Stats requests",
      empty: "There are no active requests",
      reject: "Reject",
      approve: "Approve",
      approved: "Request was successfully approved.",
      rejected: "Request was rejected.",
    },
    stats: {
      totalUsers: "Total users",
      newUsersToday: "New users today",
      ticketsToday: "Tickets today",
      oddsUpdatedAt: "Odds updated",
      totalUsersSub: "all time",
      newUsersTodaySub: "since 00:00",
      ticketsTodaySub: "placed",
    },
    users: {
      title: "User management",
      loadMore: "Load more",
      searchPlaceholder: "Search users...",
      userHeader: "User",
      lastLoginHeader: "Last login",
      joinedHeader: "Joined",
      notFound: "User not found",
    },
  },
  assistant: {
    risk: { safe: "Safe", balanced: "Balanced", aggressive: "Aggressive" },
    state: {
      aiUnavailable: "AI analysis is temporarily unavailable — try again in a few minutes.",
      limitReached: "You've used all of today's AI actions — your limit resets tomorrow.",
      noOddsYet: "Odds aren't live for this match yet — check back closer to kickoff.",
      noDataAnchor: "Limited recent history — showing market signal only.",
      notEnoughCandidates: "Not enough fixtures in your window. Try a wider day chip.",
      noCandidatesWithLiveOdds: "No fixtures with live odds in this window yet.",
      aiPicksBelowMinimum: "Couldn't lock in enough picks. Try a different risk profile.",
      noUpcomingMatches: "No fixtures in your selected window.",
      noMatchesPassedChips: "No fixtures match all your chips. Try removing one.",
      noMatchesWithLiveOdds: "Matches found, but odds aren't live for any of them yet.",
      aiPicksFilteredOut: "AI couldn't lock in bookable picks. Try different chips.",
    },
    aiInsight: "AI insight",
    formAria: "Form, oldest to most recent: {{value}}",
    riskSignalsAria: "Risk signals",
    smartStake: {
      aria: "Suggested stake",
      yourStake: "Your stake",
      suggested: "Suggested",
    },
    briefing: {
      title: "Match Briefing",
      notFound: "Match not found",
      notFoundDesc: "The fixture may have moved or been removed.",
      loadError: "Couldn't load the briefing",
      loadErrorDesc: "Try again in a moment.",
      suggestedPicks: "Suggested picks",
      noPicksNote:
        "The AI didn't find a pick worth backing at these odds — the analysis above still stands.",
      h2h: "Head-to-head",
      noMeetings: "No recent meetings.",
      form: "Form",
      record: "Record",
      home: "Home",
      away: "Away",
      goals: "Goals",
      goalsValue: "{{scored}} scored · {{conceded}} conceded",
      vs: "vs ",
      at: "@ ",
      players: "Players",
      statsPending: "Player stats refresh pending",
      surface: "Surface",
      surfaceAria: "Surface: {{label}}",
      hard: "Hard",
      clay: "Clay",
      grass: "Grass",
      hardIndoor: "Hard (Indoor)",
      carpet: "Carpet",
      seasonLabel: "{{year}} season",
      season: "Season",
      surfaceStatAria: "{{label}}: {{won}} wins, {{lost}} losses",
    },
    builder: {
      sport: "Sport",
      day: "Day",
      legs: "Legs",
      risk: "Risk",
      today: "Today",
      tomorrow: "Tomorrow",
      both: "Both",
      buildError: "Couldn't build a ticket. Try again.",
      building: "Building…",
      build: "Build my ticket",
      notEnoughMatches: "Not enough matches",
      limitReachedTitle: "Daily AI limit reached",
      tryDifferent: "Try a different day or sport.",
      tryAnother: "Try another",
      added_one: "{{count}} pick added to your ticket",
      added_other: "{{count}} picks added to your ticket",
      aiPicks: "AI picks",
      legCount_one: "{{count}} leg",
      legCount_other: "{{count}} legs",
      totalOdd: "total odd {{odd}}",
      matchingFixtures_one: "Matching fixture ({{count}})",
      matchingFixtures_other: "Matching fixtures ({{count}})",
      otherCandidates_one: "Other candidate ({{count}})",
      otherCandidates_other: "Other candidates ({{count}})",
      addWholeTicket_one: "Add whole ticket ({{count}} leg)",
      addWholeTicket_other: "Add whole ticket ({{count}} legs)",
      tryAnotherBuild: "Try another build",
    },
    pattern: {
      sport: "Sport",
      day: "Day",
      today: "Today",
      tomorrow: "Tomorrow",
      both: "Both",
      todayTomorrow: "Today + Tomorrow",
      chipGroup: { form: "Form", goals: "Goals", h2h: "H2H" },
      chips: {
        home_team_winning_form: "Home team in form",
        home_team_unbeaten: "Home team unbeaten",
        away_team_winning_form: "Away team in form",
        away_team_unbeaten: "Away team unbeaten",
        high_scoring_teams: "High-scoring teams",
        low_scoring_teams: "Low-scoring teams",
        both_teams_score: "Both teams score often",
        h2h_one_sided: "One-sided H2H",
        h2h_recent_reversal: "Recent H2H reversal",
      },
      hint: "Pick 1-{{max}} chips. Selected: {{selected}}/{{max}}",
      searchError: "Couldn't search patterns. Try again.",
      searching: "Searching…",
      findMatches: "Find matches",
      noFixturesMatched: "No fixtures matched",
      noFixturesMatch: "No fixtures match.",
      adjustChips: "Adjust chips",
      topPicks: "Top picks",
      allFixtures_one: "All matching fixtures ({{count}})",
      allFixtures_other: "All matching fixtures ({{count}})",
      tryAnotherSearch: "Try another search",
      funnelTitle: "Fixtures passing each chip",
      formLabel: "Form: {{home}} / {{away}}",
      formTitle: "Form, oldest to most recent",
      goalsLabel: "Goals: {{home}} / {{away}}",
    },
    sanity: {
      headsUp: "Heads-up",
      signalsTripped: "Some risk signals tripped ({{signals}}).",
      reviewStake: "review your stake",
      use: "Use {{amount}}",
      cancel: "Cancel",
      placeAnyway: "Place anyway",
      metrics: {
        stakeVsBankroll: "Stake vs bankroll",
        stakeVsAvg: "Stake vs 30d avg",
        recentRecord: "Recent record",
        lossStreak: "Loss streak",
      },
      signals: {
        stake_vs_bankroll_high: "Stake is a large share of your bankroll",
        stake_vs_bankroll_elevated: "Stake is above your usual bankroll share",
        stake_vs_avg_high: "Stake well above your 30-day average",
        bracket_low_winrate: "Low win rate at this stake bracket",
        loss_streak: "On a loss streak",
        long_loss_streak: "On a long loss streak",
      },
      basis: {
        bankroll_ceiling: "Capped at a safe share of your bankroll",
        avg_stake_baseline: "Matches your usual stake size",
        loss_streak_discount: "Halved after your recent loss streak",
        bracket_history_discount: "Trimmed by your record at these odds",
        minimum_stake_floor: "The platform minimum stake",
        no_history_default: "Our default for new bettors",
        fallback: "A safer stake for this bet",
      },
    },
    reasoning: {
      title: "Why this ticket?",
      subtitle: "Optional — helps your retrospective.",
      selected_one: "{{count}}/{{max}} selected",
      selected_other: "{{count}}/{{max}} selected",
      skip: "Skip",
      saving: "Saving…",
      saveTags: "Save tags",
      saved: "Reasoning saved",
      tags: {
        home_form: "Home form",
        away_form: "Away form",
        h2h: "H2H",
        key_player: "Key player",
        recent_results: "Recent results",
        value_odd: "Value odd",
        gut_call: "Gut call",
        tipster_pick: "Tipster pick",
        statistical_edge: "Statistical edge",
      },
    },
    chip: {
      riskSafe: "SAFE",
      riskBalanced: "BALANCED",
      riskAggressive: "AGGRESSIVE",
      marketOnly: "market-only",
      marketConsensus: "Derived from market consensus — not a form-anchored AI read",
      book: "book",
      books: "books",
      value: "value {{ev}}",
      valueTitle:
        "Expected value at the best price, with the bookmaker margin removed — the price pays more than the fair odds",
      onTicket: "On ticket",
      addToTicket: "Add to ticket",
      rankAria: "Rank {{rank}}",
      oddAria: "Odd {{odd}}",
      pickAria: "{{risk}} pick: {{home}} vs {{away}}, {{market}} {{selection}} at {{odd}}",
    },
    crowd: {
      community: "Community",
      notEnough: "Not enough bets on this match yet — check back closer to kickoff.",
      betsCount_one: "{{count}} bet",
      betsCount_other: "{{count}} bets",
      bettorsCount_one: "{{count}} bettor",
      bettorsCount_other: "{{count}} bettors",
      mostBacked: "Most-backed bets on this match",
      sharpPick: "sharp pick",
      sharpPickTitle: "Sharp pick — {{pct}}% of backers are profitable long-term",
      bettor: "bettor",
      bettors: "bettors",
      avg: "avg {{units}}",
      badge: {
        backed_one: "{{count}} backed",
        backed_other: "{{count}} backed",
        sharp: "sharp",
        sharpTitle: "Sharp money agrees — {{pct}}% of backers are profitable long-term",
        aria_one: "{{count}} bettor backed this match, {{pct}} percent on {{selection}}",
        aria_other: "{{count}} bettors backed this match, {{pct}} percent on {{selection}}",
        ariaSharp_one:
          "{{count}} bettor backed this match, {{pct}} percent on {{selection}}. Sharp money agrees: {{sharpPct}} percent of those backers are profitable long-term.",
        ariaSharp_other:
          "{{count}} bettors backed this match, {{pct}} percent on {{selection}}. Sharp money agrees: {{sharpPct}} percent of those backers are profitable long-term.",
      },
    },
    bettors: {
      similar: "Bettors like you, doing better",
      topPerformers: "Top performers",
      topPerformersIn: "Top performers in {{sport}}",
      aria: "Bettors to follow",
      viewProfile: "View {{name}}'s profile",
    },
    motd: {
      todaysPicks: "Today's Picks",
      matchOfTheDay: "Match of the Day",
      more: "More",
      marketConsensus: "Derived from market consensus — not a form-anchored AI read",
      marketOnly: "market-only",
      value: "value {{ev}}",
      valueTitle:
        "Expected value at the best price, with the bookmaker margin removed — the price pays more than the fair odds",
      onTicket: "On ticket",
      add: "Add",
    },
    trending: {
      now: "Trending now",
      aria: "Trending matches",
      betsCount_one: "{{count}} bet",
      betsCount_other: "{{count}} bets",
      bettorsCount_one: "{{count}} bettor",
      bettorsCount_other: "{{count}} bettors",
    },
    topTickets: {
      loadError: "Couldn't load top tickets",
      loadErrorDesc: "Try again in a moment.",
      empty: "No top tickets yet",
      emptyDesc: "Once tickets start getting reactions, the most-liked ones show up here.",
      meta: "{{bets}} bets · odd {{odd}} · stake {{stake}} U · {{date}}",
    },
    aiAssistant: "BetRight AI Assistant",
    tools: { buildTicket: "Build me a ticket", findPattern: "Find matching games" },
    trend: {
      insights: "Insights",
      loadError: "Couldn't load your trend",
      loadErrorDesc: "Try again in a moment.",
      notEnough: "Not enough history yet",
      notEnoughDesc:
        "Once you've settled a few more tickets, we'll show where you're hot, where you're cold, and what to change next.",
      lastDays: "Last {{days}} days",
      aiUnavailable: "AI read is unavailable right now — your numbers are below.",
      whatToDo: "What to do next",
      hot: "Hot",
      cold: "Cold",
      profitCurve: "Profit curve · cumulative U",
      profitCurveAria: "Cumulative profit over your settled bets",
      curveStart: "Start",
      curveTotal: "Total",
      curveTicket: "This ticket",
      breakdownAria: "Performance breakdown axis",
      trendColumn: "Trend",
      axis: {
        sport: "Sport",
        oddsRange: "Odds range",
        league: "League",
        timeOfDay: "Time of day",
        dayOfWeek: "Day of week",
        reasoning: "Reasoning",
      },
      summary: { bets: "Bets", winRate: "Win rate", roi: "ROI", profit: "Profit" },
      table: { bets: "Bets", wl: "W-L", winPct: "Win %", roi: "ROI", profit: "Profit" },
      performanceBy: "Performance by {{axis}}",
      noSegment: "No settled bets to break down by {{axis}} yet.",
    },
  },
  notifications: {
    title: "Notifications",
    deleteAll: "Delete all notifications",
    empty: "You don't have any notifications.",
    bellAria: "Notifications",
    readAll: "Mark all read",
    close: "Close",
    loadMore: "Load more",
    loading: "Loading...",
  },
  errors: {
    api: {
      VALIDATION_ERROR: "Some fields are invalid.",
      BAD_REQUEST: "Request could not be processed.",
      UNAUTHORIZED: "You need to sign in to do that.",
      FORBIDDEN: "You don't have permission to do that.",
      NOT_FOUND: "We couldn't find what you were looking for.",
      CONFLICT: "That conflicts with something already there.",
      RATE_LIMIT_EXCEEDED: "Too many attempts. Please try again later.",
      INTERNAL_SERVER_ERROR: "Something went wrong on our side.",
      NETWORK_ERROR: "Network error. Check your connection and try again.",
      UNKNOWN: "Something went wrong.",
    },
    usernameRequired: "Username is required.",
    passwordRequired: "Password is required.",
    emailValid: "Email must be valid.",
    emailInvalid: "Invalid email address.",
    passwordMin: "Password must be at least 10 characters.",
    passwordMax: "Password must be at most 64 characters.",
    passwordLong: "Password is too long.",
    confirmPassword: "Please confirm your password.",
    confirmPasswordMin: "Confirm password must be at least 10 characters.",
    passwordsMatch: "Passwords must match.",
    currentPasswordRequired: "Please enter your current password.",
    newPasswordDiffer: "New password must differ from the current one.",
    usernameShort: "The username is too short.",
    usernameLong: "The username is too long.",
    usernameMin: "Username must be at least 3 characters.",
    usernameMax: "Username must be at most 15 characters.",
    emailValidAddress: "Please enter a valid email address.",
    firstNameRequired: "First name is required.",
    firstNameLong: "First name is too long.",
    lastNameRequired: "Last name is required.",
    lastNameLong: "Last name is too long.",
  },
};

// RO catalogue is filled during the translation pass; until then RO falls back
// to EN (fallbackLng below).
const ro = {
  common: {
    language: { en: "EN", ro: "RO", label: "Limbă" },
    theme: { toggle: "Comută tema întunecată", label: "Temă" },
    sports: {
      football: "Fotbal",
      basketball: "Baschet",
      tennis: "Tenis",
      handball: "Handbal",
      hockey: "Hochei",
      baseball: "Baseball",
      rugby: "Rugbi",
      americanfootball: "Fotbal american",
      volleyball: "Volei",
    },
    // Numele piețelor + selecțiile, traduse pentru afișare. Cheile sunt
    // string-urile exacte din feed (vezi utils/marketLabels.ts); liniile
    // numerice (ex. „2.5") rămân neschimbate.
    market: {
      names: {
        "Match Winner": "Câștigător meci",
        Winner: "Câștigător",
        "Home/Away": "Gazde/Oaspeți",
        "1st Half Winner": "Câștigător repriza 1",
        "2nd Half Winner": "Câștigător repriza 2",
        "Goals Over/Under": "Peste/Sub Goluri",
        "Goals Over/Under 1st Half": "Peste/Sub Goluri Repriza 1",
        "Over/Under": "Peste/Sub",
        "Over/Under by Games in Match": "Peste/Sub Game-uri în Meci",
        "Asian Handicap": "Handicap Asiatic",
        "Asian Handicap (Sets)": "Handicap Asiatic (Seturi)",
        "Asian Handicap (Games)": "Handicap Asiatic (Game-uri)",
        "Set Betting": "Scor pe Seturi",
        "Total - Home": "Total Gazde",
        "Total - Away": "Total Oaspeți",
        "Corners Over Under": "Peste/Sub Cornere",
        "HT/FT Double": "Pauză/Final",
        "Both Teams To Score": "Ambele Echipe Marchează",
        "Double Chance": "Șansă Dublă",
        "Highest Scoring Half": "Repriza cu cele mai multe goluri",
        "Odd/Even": "Impar/Par",
        "Correct Score": "Scor Corect",
        "Draw No Bet": "Pariu fără Egal",
      },
      sides: {
        Home: "Gazde",
        Away: "Oaspeți",
        Draw: "Egal",
        Over: "Peste",
        Under: "Sub",
        Yes: "Da",
        No: "Nu",
        Odd: "Impar",
        Even: "Par",
        "1st Half": "Repriza 1",
        "2nd Half": "Repriza 2",
        "Home/Draw": "Gazde/Egal",
        "Draw/Away": "Egal/Oaspeți",
        "Home/Away": "Gazde/Oaspeți",
      },
      categories: {
        Main: "Principale",
        "Over/Under": "Peste/Sub",
        Handicap: "Handicap",
        Players: "Jucători",
        Combo: "Combinate",
      },
    },
    nav: {
      betfeed: "Feed",
      matches: "Meciuri",
      standings: "Clasament",
      bookmakers: "Case de pariuri",
      learn: "Ghid",
      admin: "Panou",
    },
    userMenu: { myProfile: "Profilul meu", logout: "Deconectare" },
    footer: {
      telegramPitch:
        "Vino pe canalul de Telegram unde primești notificări instant de fiecare dată când cei mai buni pontatori postează un pont nou",
      joinTelegram: "Alătură-te pe Telegram",
      usefulPages: "Pagini utile",
      contact: "Contact",
      terms: "Termeni și condiții",
      privacy: "Politica de confidențialitate",
      followUs: "Urmărește-ne",
    },
    whatsNew: {
      title: "Ce e nou în V2",
      heading: "Ce e nou în V2.0",
      cta: "Să începem!",
      features: [
        "🤖 Analize AI ale meciurilor — formă, întâlniri directe și ponturi",
        "🤖 Generator de bilete cu AI și căutare după modele",
        "🤖 Meciurile zilei recomandate de inteligența artificială",
        "🤖 „Pariori ca tine” — recomandări personalizate",
        "🤖 Tendința ta — vezi unde ești în formă și unde nu",
        "🤖 Vezi cum pariază comunitatea la fiecare meci",
        "🤖 Verificare înainte de pariu și miză sugerată",
        "🌙 Mod întunecat",
        "🌐 Suport pentru limba română",
        "🆕 Adăugate cele mai importante case de pariuri",
        "🆕 Filtre de comparație și case de pariuri",
        "🆕 Poți alege cea mai bună cotă pentru pariul tău favorit",
        "🆕 Mai multe piețe de pariuri pentru toate sporturile",
        "🆕 Notificări în browser",
        "🆕 Resetare buget de maxim 3 ori - cu aprobarea adminului",
        "🆕 Încărcare poză de profil",
        "🆕 Design responsive pe mobil și tabletă",
      ],
    },
    standings: {
      title: "Cei mai buni pariori",
      eyebrow: "Clasament",
      tagline:
        "Clasați după performanța verificată și decontată. Urmărește-i pe cei mai buni și copiază-le analizele.",
      empty: "Încă niciun parior",
      emptyDesc: "După ce pariorii își decontează biletele, clasamentul se completează aici.",
      winRate: "Rată câștig",
      tickets: "Bilete",
      scopeAllTime: "Toate",
      scope90: "90 zile",
      scope30: "30 zile",
      scopeAria: "Interval de timp",
      loading: "Se încarcă clasamentul",
      subtitle: "Clasament general",
      loadMore: "Încarcă mai multe",
    },
    bookmakers: {
      title: "Top case de pariuri",
      loadError: "Nu am putut încărca casele de pariuri",
      loadErrorDesc: "A apărut o eroare la încărcarea listei. Te rugăm să încerci din nou.",
      empty: "Încă nu există case de pariuri",
      emptyDesc: "Revino curând — casele de pariuri vor apărea aici când vor fi disponibile.",
      featured: "Recomandat",
      featuredRated: "Recomandat · #1 ca rating",
      moreBookmakers: "Mai multe case de pariuri",
      rated: "#{{rank}} ca rating",
      visitNamed: "Vizitează {{name}}",
      readReview: "Citește recenzia",
      metricRating: "Rating",
      metricPayout: "Rată plată",
      metricSpeed: "Timp plată",
      visitSite: "Vizitează site-ul",
      noReviews: "Încă fără recenzii",
      reviewsCount_one: "{{count}} recenzie",
      reviewsCount_few: "{{count}} recenzii",
      reviewsCount_other: "{{count}} de recenzii",
      filtersAria: "Filtrează casele de pariuri",
      filterHighRated: "Bine cotate",
      filterTopPayout: "Plată generoasă",
      filterHasBonus: "Cu bonus",
      noMatch: "Nicio casă de pariuri nu corespunde",
      noMatchDesc: "Încearcă să elimini un filtru pentru a vedea mai multe.",
      reviewsTitle: "Recenzii {{name}}",
      writeReview: "Scrie o recenzie",
      editReview: "Editează recenzia ta",
      yourRating: "Evaluarea ta",
      starAria: "Acordă {{n}} stele",
      reviewPlaceholder: "Împărtășește-ți experiența (opțional)",
      submitReview: "Trimite recenzia",
      updateReview: "Actualizează recenzia",
      deleteReview: "Șterge recenzia",
      reviewError: "Nu am putut salva recenzia. Te rugăm să încerci din nou.",
      reviewsLoading: "Se încarcă recenziile…",
      noReviewsYet: "Încă fără recenzii. Fii primul care lasă o recenzie.",
      you: "Tu",
      loadMore: "Încarcă mai multe",
    },
    learn: {
      title: "Ghid",
      subtitle: "Explicații de piață și concepte de pariere care îți ascut analizele.",
      loadError: "Nu am putut încărca secțiunea Ghid",
      loadErrorDesc: "Verifică-ți conexiunea și încearcă din nou.",
      empty: "Încă nimic aici",
      emptyDesc: "Conținutul educațional va apărea aici după publicare.",
      articles: "Articole",
      formula: "Formulă",
      workedExample: "Exemplu practic",
      related: "Similare",
    },
    chrome: {
      logoAlt: "BetRight",
      openMenu: "Deschide meniul",
      openProfileMenu: "Deschide meniul de profil",
      openUserMenu: "Deschide meniul utilizatorului",
      openSearch: "Deschide căutarea",
      openTicket: "Deschide biletul",
      closeDialog: "Închide fereastra",
      home: "Acasă",
      notifications: "Notificări",
      all: "Toate",
      orderByLabel: "Ordonează după:",
      orderBy: { PROFIT: "Profit", ROI: "ROI", YIELD: "Randament" },
      allSports: "Toate sporturile",
      allSportsTooltip: "Toate sporturile",
      moreSports: "Mai multe sporturi",
      allBookmakersTooltip: "Toate casele",
      moreBookmakers: "Mai multe case de pariuri",
      comparison: "COMPARAȚIE",
      previousDay: "Ziua precedentă",
      nextDay: "Ziua următoare",
      brief: "Analiză",
      removeFromTicket: "Elimină de pe bilet",
      ticketAdded: "Biletul a fost adăugat cu succes",
      ticketAddedAdjusted: "Plasat la cota finală {{odd}} — câștig potențial {{payout}} U",
      insufficientBankroll: "Bancă insuficientă. Te rugăm să soliciți resetarea băncii",
      maxBetsLimit: "Ai atins limita maximă de {{max}} meciuri",
      social: {
        facebook: "BetRight pe Facebook",
        instagram: "BetRight pe Instagram",
        tiktok: "BetRight pe TikTok",
      },
      notifPrompt: {
        title: "Activezi notificările din browser?",
        yes: "Da",
        no: "Nu",
      },
      push: {
        activated: "Notificările push au fost activate",
        denied: "Permisiune refuzată.",
        deniedManual: "Permisiune refuzată. Poți activa manual notificările din browser",
        deniedSettings:
          "Permisiunea a fost refuzată. Va trebui să activezi notificările pentru acest site din setările browserului.",
        notSupported: "Notificările push nu sunt acceptate în acest browser.",
        notConfigured: "Notificările push nu sunt configurate pentru această versiune.",
        enabled: "Notificări push activate.",
        disabled: "Notificări push dezactivate.",
      },
    },
    errorPages: {
      pageError: "Ceva nu a mers bine pe această pagină.",
      pageErrorHint: "Am înregistrat eroarea. Poți reîncerca — datele recente vor fi reîncărcate.",
      tryAgain: "Încearcă din nou",
      errorTitle: "Ceva nu a mers bine",
      errorDesc:
        "Ne pare rău, a apărut o problemă la procesarea acțiunii tale. Lucrăm să o reparăm cât mai curând.",
      forbidden: "Acces interzis",
      forbiddenDesc: "Nu ai acces la această pagină.",
      notFound: "Pagină negăsită",
      notFoundDesc: "Pagina pe care încerci să o accesezi nu este disponibilă.",
      backHome: "Înapoi la pagina principală",
    },
    blog: {
      comingSoon: "Blogul vine în curând",
      comingSoonDesc: "Încă lucrăm la asta — revino în curând.",
    },
  },
  auth: {
    login: {
      title: "Autentificare",
      welcomeTitle: "Bine ai revenit",
      newUser: "Utilizator nou?",
      registerLink: "Înregistrare",
      username: "Nume utilizator",
      password: "Parolă",
      submit: "Autentificare",
      forgot: "Ți-ai uitat parola?",
    },
    register: {
      title: "Înregistrare",
      createTitle: "Creează-ți contul",
      haveAccount: "Ai deja un cont?",
      loginLink: "Autentificare",
      username: "Nume utilizator",
      firstName: "Prenume",
      lastName: "Nume",
      email: "Email",
      password: "Parolă",
      confirmPassword: "Confirmă parola",
      submit: "Înregistrare",
      terms:
        "Prin crearea contului confirm că am cel puțin 18 ani și sunt de acord cu Termenii și Condițiile BETRIGHT și cu Politica de confidențialitate.",
    },
    forgot: {
      title: "Resetează parola",
      intro: "Introdu adresa de email asociată contului tău.",
      email: "Email",
      submit: "Trimite linkul de resetare",
      backToLogin: "Înapoi la autentificare",
      sent: "Dacă există un cont pentru acel email, a fost trimis un link de resetare. Verifică-ți inboxul.",
      sentTitle: "Verifică-ți emailul",
      invalidEmail: "Te rugăm să introduci o adresă de email validă.",
    },
    reset: {
      title: "Setează o parolă nouă",
      subtitle: "Alege o parolă puternică pe care nu o folosești în altă parte.",
      newPassword: "Parolă nouă",
      confirmPassword: "Confirmă parola nouă",
      submit: "Resetează parola",
      success: "Parola ta a fost resetată. Te rugăm să te autentifici.",
      invalidTitle: "Link de resetare invalid",
      invalidToken:
        "Acest link de resetare lipsește sau este malformat. Te rugăm să soliciți unul nou.",
      requestNew: "Solicită un link nou",
    },
    password: {
      show: "Afișează parola",
      hide: "Ascunde parola",
      weak: "Slabă",
      fair: "Medie",
      strong: "Puternică",
      hint: "Folosește 8+ caractere cu o cifră și un simbol.",
    },
    brand: {
      eyebrow: "Pariori experți · din 2024",
      headlineLead: "Urmărește fiecare pariu.",
      headlineEmphasis: "Pariază ca experții.",
      sub: "Urmărește cei mai profitabili pontatori, primești analize AI la fiecare meci și vezi ROI-ul real — nu doar victoriile.",
      feature1: "ROI verificat pentru fiecare pontator",
      feature2: "Analize AI la fiecare meci",
      feature3: "Semnale din comunitate și de la experți",
      trust: "Alături de pariori experți care urmăresc ROI real în fiecare zi.",
    },
  },
  bets: {
    filter: {
      latest: "Recente",
      following: "Urmărite",
      winners: "Câștigătoare",
      top: "Top",
      tipLatest: "Cele mai recente bilete",
      tipFollowing: "Vezi biletele celor pe care îi urmărești",
      tipFollowingEmpty: "Nu urmărești niciun utilizator",
      tipWinners: "Vezi biletele câștigătoare",
      tipTop: "Cele mai apreciate bilete din ultimele 24h",
    },
    loadMore: "Încarcă mai multe",
    loading: "Se încarcă...",
    loadingTickets: "Se încarcă biletele",
    feed: {
      noTicketsFromSport: "Niciun bilet la {{sport}}",
      filterEmptyDesc: "Încearcă alt sport sau elimină filtrul.",
      resetSportFilter: "Resetează filtrul de sport",
      noTicketsYet: "Niciun bilet încă",
      noTicketsDesc: "Biletele distribuite de tine și de cei pe care îi urmărești vor apărea aici.",
    },
    ticket: {
      title: "Bilet",
      bankroll: "BUGET",
      odd: "Cotă",
      totalOdd: "Cotă totală",
      potentialReturn: "Câștig potențial",
      close: "Închide",
      clearAll: "Șterge tot",
      emptyTitle: "Biletul tău e gol",
      emptyHint: "Atinge un meci sau un pont pentru a începe biletul.",
      analysisPlaceholder: "Scrie analiza biletului (opțional)",
      stake: "Miză",
      stakeRange: "(min: 0.5 - max: 100 U)",
      quickStake: "{{amount}} U",
      delete: "Șterge biletul",
      place: "Plasează biletul",
      placeWithReturn: "Plasează biletul · {{amount}} U",
      removeFromTicket: "Elimină din bilet",
    },
    row: { copyBet: "Copiază pariul", betAdded: "Pariu adăugat" },
    card: {
      stake: "MIZĂ",
      analysis: "Analiză",
      odd: "Cotă",
      ticketRemoved: "Biletul a fost șters cu succes.",
      deleteTicket: "Șterge biletul",
      removeTicket: "Elimină acest bilet",
      alreadyCopied: "Bilet deja copiat",
      copyTicket: "Copiază biletul",
      copyTicketAria: "Copiază acest bilet în constructorul tău",
      viewUser: "Vezi detaliile utilizatorului",
      viewUserAria: "Vezi profilul lui {{name}}",
      unfollow: "Nu mai urmări",
      unfollowAria: "Nu mai urmări acest utilizator",
      follow: "Urmărește",
      followAria: "Urmărește acest utilizator",
      likes: "Aprecieri bilet",
      unlike: "Retrage aprecierea",
      like: "Apreciază acest bilet",
    },
  },
  matches: {
    title: "Program și meciuri",
    loading: "Se încarcă meciurile",
    aiToolsError:
      "Instrumentele AI nu s-au putut încărca. Reîmprospătează pagina pentru a reîncerca.",
    matchStarted: "Acest meci a început deja — nu mai poți paria pe el.",
    brief: "Analiză",
    briefAria: "Analiză AI pentru acest meci",
    briefForMatch: "Analiză AI pentru {{home}} vs {{away}}",
    community: "Comunitate",
    communityPulse: "Pulsul comunității",
    betsCount_one: "{{count}} pariu",
    betsCount_few: "{{count}} pariuri",
    betsCount_other: "{{count}} de pariuri",
    bettorsCount_one: "{{count}} parior",
    bettorsCount_few: "{{count}} pariori",
    bettorsCount_other: "{{count}} de pariori",
    crowdCheck: "Verificare comunitate",
    crowdInsight_one:
      "{{count}} parior a pariat pe acest meci. Mulțimea înclină {{pct}}% spre {{selection}}.",
    crowdInsight_few:
      "{{count}} pariori au pariat pe acest meci. Mulțimea înclină {{pct}}% spre {{selection}}.",
    crowdInsight_other:
      "{{count}} de pariori au pariat pe acest meci. Mulțimea înclină {{pct}}% spre {{selection}}.",
    crowdInsightSharp_one:
      "{{count}} parior a pariat pe acest meci. Mulțimea înclină {{pct}}% spre {{selection}} — iar banii inteligenți confirmă.",
    crowdInsightSharp_few:
      "{{count}} pariori au pariat pe acest meci. Mulțimea înclină {{pct}}% spre {{selection}} — iar banii inteligenți confirmă.",
    crowdInsightSharp_other:
      "{{count}} de pariori au pariat pe acest meci. Mulțimea înclină {{pct}}% spre {{selection}} — iar banii inteligenți confirmă.",
    crowdEmptyTitle: "Încă nu sunt destule pariuri",
    crowdEmptyDesc:
      "Semnalul comunității apare după ce pariorii pariază pe acest meci — revino mai aproape de start.",
    mostBacked: "Cele mai pariate azi",
    mostBackedEmpty: "Încă niciun pariu — revino mai aproape de start.",
    communityEmpty: "Niciun meci în tendințe încă.",
    last24h: "24h",
    backedCount_one: "{{count}} pariază",
    backedCount_few: "{{count}} pariază",
    backedCount_other: "{{count}} pariază",
    openOdds: "Deschide cotele pentru {{home}} vs {{away}}",
    openBriefingFor: "Deschide analiza pentru {{home}} vs {{away}}",
    sharp: "Sharp",
    seeAllOdds: "Vezi toate cotele",
    surfaceAria: "Suprafață: {{label}}",
    surfaceLabel: {
      clay: "Zgură",
      hard: "Hard",
      "hard-indoor": "Hard (Indoor)",
      grass: "Iarbă",
      carpet: "Covor",
    },
    search: {
      topSearches: "Căutări populare",
      placeholder: "Caută pariori, meciuri, ligi…",
      close: "Închide căutarea",
      hintTitle: "Caută în BetRight",
      hintDesc: "Găsește pariori, meciuri și ligi.",
      noResults: "Niciun rezultat găsit",
      noResultsDesc: "Încearcă alt termen de căutare.",
      noToday: "Niciun meci astăzi",
      noTodayDesc: "Revino mai târziu, pe măsură ce se programează mai multe meciuri.",
      roi: "ROI",
      groups: {
        bettors: "Pariori",
        matches: "Meciuri astăzi",
        leagues: "Ligi",
      },
    },
    empty: {
      noMatchesSportDate: "Niciun meci de {{sport}} în {{date}}",
      trySportDate: "Încearcă alt sport sau altă dată.",
      resetSport: "Resetează filtrul de sport",
      noMatchesDate: "Niciun meci în {{date}}",
      pickDate: "Alege altă dată din selector.",
    },
  },
  profile: {
    loading: "Se încarcă profilul",
    unavailable: "Profil indisponibil",
    unavailableDesc: "Nu am putut încărca profilul tău acum. Te rugăm să reîmprospătezi.",
    userNotFound: "Utilizator negăsit",
    userNotFoundDesc: "Utilizatorul {{name}} nu a fost găsit.",
    backHome: "Înapoi la pagina principală",
    followersCount_one: "Urmăritor: {{count}}",
    followersCount_few: "Urmăritori: {{count}}",
    followersCount_other: "Urmăritori: {{count}}",
    registeredIn: "Înregistrat în {{date}}",
    requestReset_one: "Solicită resetarea statisticilor {{count}}/3",
    requestReset_few: "Solicită resetarea statisticilor {{count}}/3",
    requestReset_other: "Solicită resetarea statisticilor {{count}}/3",
    editProfile: "Editează profilul",
    signOutEverywhere: "Deconectare de peste tot",
    signOutConfirm:
      "Te deconectezi de pe toate dispozitivele pe care ești autentificat? Va trebui să te autentifici din nou pe fiecare.",
    resetRequestSent: "Solicitarea a fost trimisă administratorilor.",
    enableNotifications: "Activează notificările",
    disableNotifications: "Dezactivează notificările",
    follow: "Urmărește",
    unfollow: "Nu mai urmări",
    following: "Urmărit",
    followUserAria: "Urmărește pe {{name}}",
    unfollowUserAria: "Nu mai urmări pe {{name}}",
    stat: {
      profit: "Profit",
      tickets: "Bilete",
      roi: "ROI",
      yield: "Randament",
      bankroll: "Buget",
    },
    tab: {
      winRate: "Rată câștig",
      betsHistory: "Istoric pariuri",
      followers: "Urmăritori",
      following: "Urmărește",
      insights: "Statistici",
    },
    table: {
      bettor: "Parior",
      winRate: "Rată câștig",
    },
    winRate: {
      label: "Rată câștig {{rate}} %",
      wins_one: "Câștigat: {{count}}",
      wins_few: "Câștigate: {{count}}",
      wins_other: "Câștigate: {{count}}",
      lost_one: "Pierdut: {{count}}",
      lost_few: "Pierdute: {{count}}",
      lost_other: "Pierdute: {{count}}",
      avgStake: "Miză medie",
      avgOdd: "Cotă medie",
      chartTitle: "Rată câștig pe lună",
      chartAria: "Rată câștig pe lună",
    },
    strengths: {
      title: "Puncte forte pe sport",
      empty: "Încă nu sunt destule bilete decise pentru o defalcare pe sport.",
      wr: "{{rate}}% RC",
      roi: "{{roi}}% ROI",
      sample_one: "{{count}} bilet",
      sample_few: "{{count}} bilete",
      sample_other: "{{count}} bilete",
    },
    ticketHistory: "Istoric bilete",
    bestUsers: "Cei mai buni utilizatori",
    bettorsToFollow: "Pariori de urmărit",
    viewAll: "Toate",
    yourWeek: "Săptămâna ta",
    ticketsCount_one: "{{count}} bilet",
    ticketsCount_few: "{{count}} bilete",
    ticketsCount_other: "{{count}} de bilete",
    searchUsers: "Caută utilizatori",
    loadMore: "Încarcă mai multe",
    viewTips: "Vezi ponturile",
    viewUser: "Vezi detaliile utilizatorului",
    viewUserProfile: "Vezi profilul lui {{name}}",
    statInline: { profit: "Profit", roi: "ROI", yield: "Randament" },
    editDropdown: "Editează profilul",
    userActions: "Acțiuni utilizator",
    changePassword: {
      title: "Schimbă parola",
      current: "Parola curentă",
      newPassword: "Parolă nouă",
      confirm: "Confirmă parola",
      cancel: "Anulează",
      save: "Salvează parola nouă",
      success: "Parola a fost actualizată cu succes.",
    },
    editProfileDialog: {
      title: "Date utilizator",
      changePassword: "Schimbă parola",
      cancel: "Anulează",
      save: "Salvează modificările",
      firstName: "Prenume",
      lastName: "Nume",
      userName: "Nume utilizator",
      email: "Email",
      dataSavedAdmin: "Datele utilizatorului au fost actualizate cu succes.",
      dataSaved: "Informațiile profilului tău au fost actualizate.",
      photoSavedAdmin: "Poza utilizatorului a fost actualizată cu succes.",
      photoSaved: "Poza de profil a fost actualizată.",
    },
    uploader: {
      invalidType: "Se acceptă doar imagini JPEG, PNG, WebP sau GIF.",
      tooLarge: "Imaginea este prea mare (max {{size}}MB).",
      upload: "Încarcă poza de profil",
    },
  },
  admin: {
    header: {
      eyebrow: "Admin",
      title: "Panou",
      status: "Fluxuri active · cote {{time}}",
    },
    announcement: {
      title: "Trimite notificare",
      placeholder: "Anunțul tău",
      send: "Trimite anunțul",
      hint: "Trimis în fluxul de notificări al fiecărui utilizator.",
      sent: "Notificarea cu anunțul a fost trimisă tuturor utilizatorilor.",
    },
    endpoints: {
      title: "Telemetrie endpoint-uri",
      today: "azi",
      empty: "Niciun endpoint nu a raportat o rulare reușită astăzi încă.",
    },
    requests: {
      title: "Cereri de statistici",
      empty: "Nu există cereri active",
      reject: "Respinge",
      approve: "Aprobă",
      approved: "Cererea a fost aprobată cu succes.",
      rejected: "Cererea a fost respinsă.",
    },
    stats: {
      totalUsers: "Total utilizatori",
      newUsersToday: "Utilizatori noi azi",
      ticketsToday: "Bilete azi",
      oddsUpdatedAt: "Cote actualizate",
      totalUsersSub: "din total",
      newUsersTodaySub: "de la 00:00",
      ticketsTodaySub: "plasate",
    },
    users: {
      title: "Gestionare utilizatori",
      loadMore: "Încarcă mai multe",
      searchPlaceholder: "Caută utilizatori...",
      userHeader: "Utilizator",
      lastLoginHeader: "Ultima conectare",
      joinedHeader: "Înregistrat",
      notFound: "Utilizator negăsit",
    },
  },
  assistant: {
    risk: { safe: "Sigur", balanced: "Echilibrat", aggressive: "Agresiv" },
    state: {
      aiUnavailable: "Analiza AI este momentan indisponibilă — încearcă din nou în câteva minute.",
      limitReached: "Ai folosit toate acțiunile AI de azi — limita se resetează mâine.",
      noOddsYet: "Cotele nu sunt încă live pentru acest meci — revino mai aproape de start.",
      noDataAnchor: "Istoric recent limitat — afișăm doar semnalul pieței.",
      notEnoughCandidates:
        "Nu sunt destule meciuri în intervalul tău. Încearcă un interval de zile mai larg.",
      noCandidatesWithLiveOdds: "Niciun meci cu cote live în acest interval deocamdată.",
      aiPicksBelowMinimum: "Nu am putut fixa destule ponturi. Încearcă alt profil de risc.",
      noUpcomingMatches: "Niciun meci în intervalul selectat.",
      noMatchesPassedChips:
        "Niciun meci nu corespunde tuturor filtrelor. Încearcă să elimini unul.",
      noMatchesWithLiveOdds: "Am găsit meciuri, dar cotele nu sunt încă live pentru niciunul.",
      aiPicksFilteredOut: "AI-ul nu a putut fixa ponturi pariabile. Încearcă alte filtre.",
    },
    aiInsight: "Analiză AI",
    formAria: "Formă, de la cel mai vechi la cel mai recent: {{value}}",
    riskSignalsAria: "Semnale de risc",
    smartStake: {
      aria: "Miză sugerată",
      yourStake: "Miza ta",
      suggested: "Sugerat",
    },
    briefing: {
      title: "Analiză meci",
      notFound: "Meci negăsit",
      notFoundDesc: "Meciul s-ar putea să fi fost mutat sau eliminat.",
      loadError: "Nu am putut încărca analiza",
      loadErrorDesc: "Încearcă din nou într-o clipă.",
      suggestedPicks: "Ponturi sugerate",
      noPicksNote:
        "AI-ul nu a găsit un pariu care să merite la cotele actuale — analiza de mai sus rămâne valabilă.",
      h2h: "Întâlniri directe",
      noMeetings: "Nicio întâlnire recentă.",
      form: "Formă",
      record: "Bilanț",
      home: "Acasă",
      away: "Deplasare",
      goals: "Goluri",
      goalsValue: "{{scored}} marcate · {{conceded}} primite",
      vs: "vs ",
      at: "@ ",
      players: "Jucători",
      statsPending: "Actualizarea statisticilor jucătorului este în curs",
      surface: "Suprafață",
      surfaceAria: "Suprafață: {{label}}",
      hard: "Hard",
      clay: "Zgură",
      grass: "Iarbă",
      hardIndoor: "Hard (Indoor)",
      carpet: "Covor",
      seasonLabel: "Sezonul {{year}}",
      season: "Sezon",
      surfaceStatAria: "{{label}}: {{won}} victorii, {{lost}} înfrângeri",
    },
    builder: {
      sport: "Sport",
      day: "Zi",
      legs: "Selecții",
      risk: "Risc",
      today: "Azi",
      tomorrow: "Mâine",
      both: "Ambele",
      buildError: "Nu am putut construi un bilet. Încearcă din nou.",
      building: "Se construiește…",
      build: "Construiește-mi biletul",
      notEnoughMatches: "Nu sunt destule meciuri",
      limitReachedTitle: "Limita zilnică de AI a fost atinsă",
      tryDifferent: "Încearcă altă zi sau alt sport.",
      tryAnother: "Încearcă altul",
      added_one: "{{count}} pont adăugat pe bilet",
      added_few: "{{count}} ponturi adăugate pe bilet",
      added_other: "{{count}} de ponturi adăugate pe bilet",
      aiPicks: "Ponturi AI",
      legCount_one: "{{count}} selecție",
      legCount_few: "{{count}} selecții",
      legCount_other: "{{count}} de selecții",
      totalOdd: "cotă totală {{odd}}",
      matchingFixtures_one: "Meci potrivit ({{count}})",
      matchingFixtures_few: "Meciuri potrivite ({{count}})",
      matchingFixtures_other: "Meciuri potrivite ({{count}})",
      otherCandidates_one: "Altă opțiune ({{count}})",
      otherCandidates_few: "Alte opțiuni ({{count}})",
      otherCandidates_other: "Alte opțiuni ({{count}})",
      addWholeTicket_one: "Adaugă tot biletul ({{count}} selecție)",
      addWholeTicket_few: "Adaugă tot biletul ({{count}} selecții)",
      addWholeTicket_other: "Adaugă tot biletul ({{count}} de selecții)",
      tryAnotherBuild: "Încearcă altă construcție",
    },
    pattern: {
      sport: "Sport",
      day: "Zi",
      today: "Azi",
      tomorrow: "Mâine",
      both: "Ambele",
      todayTomorrow: "Azi + Mâine",
      chipGroup: { form: "Formă", goals: "Goluri", h2h: "Întâlniri directe" },
      chips: {
        home_team_winning_form: "Gazda în formă",
        home_team_unbeaten: "Gazda neînvinsă",
        away_team_winning_form: "Oaspeții în formă",
        away_team_unbeaten: "Oaspeții neînvinși",
        high_scoring_teams: "Echipe cu multe goluri",
        low_scoring_teams: "Echipe cu puține goluri",
        both_teams_score: "Ambele echipe înscriu des",
        h2h_one_sided: "Întâlniri directe dezechilibrate",
        h2h_recent_reversal: "Răsturnare recentă în întâlniri directe",
      },
      hint: "Alege 1-{{max}} filtre. Selectate: {{selected}}/{{max}}",
      searchError: "Nu am putut căuta tipare. Încearcă din nou.",
      searching: "Se caută…",
      findMatches: "Caută meciuri",
      noFixturesMatched: "Niciun meci potrivit",
      noFixturesMatch: "Niciun meci nu corespunde.",
      adjustChips: "Ajustează filtrele",
      topPicks: "Cele mai bune ponturi",
      allFixtures_one: "Toate meciurile potrivite ({{count}})",
      allFixtures_few: "Toate meciurile potrivite ({{count}})",
      allFixtures_other: "Toate meciurile potrivite ({{count}})",
      tryAnotherSearch: "Încearcă altă căutare",
      funnelTitle: "Meciuri care trec de fiecare filtru",
      formLabel: "Formă: {{home}} / {{away}}",
      formTitle: "Formă, de la cel mai vechi la cel mai recent",
      goalsLabel: "Goluri: {{home}} / {{away}}",
    },
    sanity: {
      headsUp: "Atenție",
      signalsTripped: "Au apărut câteva semnale de risc ({{signals}}).",
      reviewStake: "verifică-ți miza",
      use: "Folosește {{amount}}",
      cancel: "Anulează",
      placeAnyway: "Plasează oricum",
      metrics: {
        stakeVsBankroll: "Miză vs bancă",
        stakeVsAvg: "Miză vs media 30z",
        recentRecord: "Bilanț recent",
        lossStreak: "Serie de înfrângeri",
      },
      signals: {
        stake_vs_bankroll_high: "Miza e o parte mare din banca ta",
        stake_vs_bankroll_elevated: "Miza e peste cota ta obișnuită din bancă",
        stake_vs_avg_high: "Miză mult peste media ta pe 30 de zile",
        bracket_low_winrate: "Rată mică de câștig la acest interval de miză",
        loss_streak: "Ești pe o serie de înfrângeri",
        long_loss_streak: "Ești pe o serie lungă de înfrângeri",
      },
      basis: {
        bankroll_ceiling: "Limitată la o parte sigură din banca ta",
        avg_stake_baseline: "Se potrivește cu miza ta obișnuită",
        loss_streak_discount: "Înjumătățită după seria ta recentă de înfrângeri",
        bracket_history_discount: "Redusă în funcție de bilanțul tău la aceste cote",
        minimum_stake_floor: "Miza minimă a platformei",
        no_history_default: "Valoarea noastră implicită pentru pariori noi",
        fallback: "O miză mai sigură pentru acest pariu",
      },
    },
    reasoning: {
      title: "De ce acest bilet?",
      subtitle: "Opțional — te ajută la retrospectivă.",
      selected_one: "{{count}}/{{max}} selectate",
      selected_few: "{{count}}/{{max}} selectate",
      selected_other: "{{count}}/{{max}} selectate",
      skip: "Omite",
      saving: "Se salvează…",
      saveTags: "Salvează etichetele",
      saved: "Motivare salvată",
      tags: {
        home_form: "Forma gazdei",
        away_form: "Forma oaspeților",
        h2h: "Întâlniri directe",
        key_player: "Jucător cheie",
        recent_results: "Rezultate recente",
        value_odd: "Cotă cu valoare",
        gut_call: "Intuiție",
        tipster_pick: "Pont de la tipster",
        statistical_edge: "Avantaj statistic",
      },
    },
    chip: {
      riskSafe: "SIGUR",
      riskBalanced: "ECHILIBRAT",
      riskAggressive: "AGRESIV",
      marketOnly: "doar piață",
      marketConsensus: "Derivat din consensul pieței — nu o analiză AI bazată pe formă",
      book: "casă",
      books: "case",
      value: "valoare {{ev}}",
      valueTitle:
        "Valoarea așteptată la cea mai bună cotă, cu marja casei eliminată — cota plătește mai mult decât e corect",
      onTicket: "Pe bilet",
      addToTicket: "Adaugă pe bilet",
      rankAria: "Locul {{rank}}",
      oddAria: "Cotă {{odd}}",
      pickAria: "Pont {{risk}}: {{home}} vs {{away}}, {{market}} {{selection}} la {{odd}}",
    },
    crowd: {
      community: "Comunitate",
      notEnough: "Încă nu sunt destule pariuri pe acest meci — revino mai aproape de start.",
      betsCount_one: "{{count}} pariu",
      betsCount_few: "{{count}} pariuri",
      betsCount_other: "{{count}} de pariuri",
      bettorsCount_one: "{{count}} parior",
      bettorsCount_few: "{{count}} pariori",
      bettorsCount_other: "{{count}} de pariori",
      mostBacked: "Cele mai pariate selecții la acest meci",
      sharpPick: "pont sharp",
      sharpPickTitle:
        "Pont sharp — {{pct}}% dintre cei care pariază sunt profitabili pe termen lung",
      bettor: "parior",
      bettors: "pariori",
      avg: "medie {{units}}",
      badge: {
        backed_one: "{{count}} pariate",
        backed_few: "{{count}} pariate",
        backed_other: "{{count}} pariate",
        sharp: "sharp",
        sharpTitle:
          "Banii sharp confirmă — {{pct}}% dintre susținători sunt profitabili pe termen lung",
        aria_one: "{{count}} parior a pariat pe acest meci, {{pct}} la sută pe {{selection}}",
        aria_few: "{{count}} pariori au pariat pe acest meci, {{pct}} la sută pe {{selection}}",
        aria_other:
          "{{count}} de pariori au pariat pe acest meci, {{pct}} la sută pe {{selection}}",
        ariaSharp_one:
          "{{count}} parior a pariat pe acest meci, {{pct}} la sută pe {{selection}}. Banii sharp confirmă: {{sharpPct}} la sută dintre acești susținători sunt profitabili pe termen lung.",
        ariaSharp_few:
          "{{count}} pariori au pariat pe acest meci, {{pct}} la sută pe {{selection}}. Banii sharp confirmă: {{sharpPct}} la sută dintre acești susținători sunt profitabili pe termen lung.",
        ariaSharp_other:
          "{{count}} de pariori au pariat pe acest meci, {{pct}} la sută pe {{selection}}. Banii sharp confirmă: {{sharpPct}} la sută dintre acești susținători sunt profitabili pe termen lung.",
      },
    },
    bettors: {
      similar: "Pariori ca tine, dar cu rezultate mai bune",
      topPerformers: "Cei mai buni pariori",
      topPerformersIn: "Cei mai buni pariori la {{sport}}",
      aria: "Pariori de urmărit",
      viewProfile: "Vezi profilul lui {{name}}",
    },
    motd: {
      todaysPicks: "Ponturile zilei",
      matchOfTheDay: "Meciul zilei",
      more: "Mai mult",
      marketConsensus: "Derivat din consensul pieței — nu o analiză AI bazată pe formă",
      marketOnly: "doar piață",
      value: "valoare {{ev}}",
      valueTitle:
        "Valoarea așteptată la cea mai bună cotă, cu marja casei eliminată — cota plătește mai mult decât e corect",
      onTicket: "Pe bilet",
      add: "Adaugă",
    },
    trending: {
      now: "În tendințe",
      aria: "Meciuri în tendințe",
      betsCount_one: "{{count}} pariu",
      betsCount_few: "{{count}} pariuri",
      betsCount_other: "{{count}} de pariuri",
      bettorsCount_one: "{{count}} parior",
      bettorsCount_few: "{{count}} pariori",
      bettorsCount_other: "{{count}} de pariori",
    },
    topTickets: {
      loadError: "Nu am putut încărca biletele de top",
      loadErrorDesc: "Încearcă din nou într-un moment.",
      empty: "Încă nu există bilete de top",
      emptyDesc: "Odată ce biletele primesc reacții, cele mai apreciate apar aici.",
      meta: "{{bets}} pariuri · cotă {{odd}} · miză {{stake}} U · {{date}}",
    },
    aiAssistant: "Asistent AI BetRight",
    tools: { buildTicket: "Construiește-mi un bilet", findPattern: "Găsește meciuri potrivite" },
    trend: {
      insights: "Statistici",
      loadError: "Nu am putut încărca tendința ta",
      loadErrorDesc: "Încearcă din nou într-un moment.",
      notEnough: "Încă nu ai suficient istoric",
      notEnoughDesc:
        "După ce mai închizi câteva bilete, îți vom arăta unde ești în formă, unde ești în scădere și ce să schimbi.",
      lastDays: "Ultimele {{days}} zile",
      aiUnavailable: "Analiza AI nu este disponibilă acum — cifrele tale sunt mai jos.",
      whatToDo: "Ce să faci în continuare",
      hot: "În formă",
      cold: "În scădere",
      profitCurve: "Curba profitului · U cumulat",
      profitCurveAria: "Profit cumulat pe pariurile tale închise",
      curveStart: "Start",
      curveTotal: "Total",
      curveTicket: "Acest bilet",
      breakdownAria: "Axă defalcare performanță",
      trendColumn: "Tendință",
      axis: {
        sport: "Sport",
        oddsRange: "Interval de cote",
        league: "Ligă",
        timeOfDay: "Ora din zi",
        dayOfWeek: "Ziua săptămânii",
        reasoning: "Motivare",
      },
      summary: { bets: "Pariuri", winRate: "Rată câștig", roi: "ROI", profit: "Profit" },
      table: { bets: "Pariuri", wl: "C-Î", winPct: "Câștig %", roi: "ROI", profit: "Profit" },
      performanceBy: "Performanță după {{axis}}",
      noSegment: "Încă nu există pariuri închise de defalcat după {{axis}}.",
    },
  },
  notifications: {
    title: "Notificări",
    deleteAll: "Șterge toate notificările",
    empty: "Nu ai nicio notificare.",
    bellAria: "Notificări",
    readAll: "Marchează tot ca citit",
    close: "Închide",
    loadMore: "Încarcă mai multe",
    loading: "Se încarcă...",
  },
  errors: {
    api: {
      VALIDATION_ERROR: "Unele câmpuri sunt invalide.",
      BAD_REQUEST: "Cererea nu a putut fi procesată.",
      UNAUTHORIZED: "Trebuie să te autentifici pentru asta.",
      FORBIDDEN: "Nu ai permisiunea să faci asta.",
      NOT_FOUND: "Nu am găsit ceea ce căutai.",
      CONFLICT: "Asta intră în conflict cu ceva existent deja.",
      RATE_LIMIT_EXCEEDED: "Prea multe încercări. Te rugăm să încerci mai târziu.",
      INTERNAL_SERVER_ERROR: "Ceva nu a mers bine la noi.",
      NETWORK_ERROR: "Eroare de rețea. Verifică-ți conexiunea și încearcă din nou.",
      UNKNOWN: "Ceva nu a mers bine.",
    },
    usernameRequired: "Numele de utilizator este obligatoriu.",
    passwordRequired: "Parola este obligatorie.",
    emailValid: "Adresa de e-mail trebuie să fie validă.",
    emailInvalid: "Adresă de e-mail invalidă.",
    passwordMin: "Parola trebuie să aibă cel puțin 10 caractere.",
    passwordMax: "Parola trebuie să aibă cel mult 64 de caractere.",
    passwordLong: "Parola este prea lungă.",
    confirmPassword: "Te rugăm să confirmi parola.",
    confirmPasswordMin: "Confirmarea parolei trebuie să aibă cel puțin 10 caractere.",
    passwordsMatch: "Parolele trebuie să coincidă.",
    currentPasswordRequired: "Te rugăm să introduci parola actuală.",
    newPasswordDiffer: "Parola nouă trebuie să fie diferită de cea actuală.",
    usernameShort: "Numele de utilizator este prea scurt.",
    usernameLong: "Numele de utilizator este prea lung.",
    usernameMin: "Numele de utilizator trebuie să aibă cel puțin 3 caractere.",
    usernameMax: "Numele de utilizator trebuie să aibă cel mult 15 caractere.",
    emailValidAddress: "Te rugăm să introduci o adresă de e-mail validă.",
    firstNameRequired: "Prenumele este obligatoriu.",
    firstNameLong: "Prenumele este prea lung.",
    lastNameRequired: "Numele este obligatoriu.",
    lastNameLong: "Numele este prea lung.",
  },
};

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { en, ro },
    ns: NAMESPACES as unknown as string[],
    defaultNS: "common",
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    nonExplicitSupportedLngs: true, // "en-US" → "en"
    interpolation: { escapeValue: false }, // React already escapes
    detection: {
      // detector → localStorage["br-locale"] → navigator.language → "en".
      order: ["localStorage", "navigator"],
      lookupLocalStorage: LOCALE_STORAGE_KEY,
      caches: ["localStorage"],
    },
  });

export default i18n;
